import React from 'react';
import {
  type Path,
  type Control,
  type FieldValues,
  useController,
} from 'react-hook-form';
import type {ActionType, ParticipantType} from '../../types/IReasons';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';

import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';

import {useRootMachine} from '../../hooks/useRootMachine';
import {selectReasonsByParticipantTypeAndActionType} from '../../selectors/reasons';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';


const DEFAULT_REASON_KEY = '1';
const TEXT_COUNT_LIMIT = 150;

type ReasonInputProps<T extends FieldValues> = {
  testId?: string;
  locked?: boolean;
  control: Control<T>;
  reasonName: Path<T>;
  required?: boolean;
  disabled?: boolean;
  actionType?: ActionType;
  participantType?: ParticipantType;
};

export const ReasonInput = <T extends FieldValues>({
  testId,
  locked = false,
  control,
  reasonName,
  required = false,
  disabled = false,
  actionType = 'cancel',
  participantType = 'CANDIDATE',
}: ReasonInputProps<T>) => {
  const {t} = useLabelTranslation();
  const {rootActor} = useRootMachine();

  const reasons = useSelector(rootActor, (state) =>
    selectReasonsByParticipantTypeAndActionType(
      state,
      participantType,
      actionType,
    ),
  );

  const reasonOptions = React.useMemo(
    () =>
      reasons.map((reason) => ({
        key: String(reason.id),
        label: reason.reason,
      })),
    [reasons],
  );

  const {field: reasonField, fieldState: reasonFieldState} = useController({
    name: reasonName,
    control,
    disabled,
  });

  const [selectedReasonKey, setSelectedReasonKey] = React.useState<
    string | null
  >(null);

  const showElaborateReason = selectedReasonKey === DEFAULT_REASON_KEY;
  const hasReasonOptions = reasons.length > 0;

  const handleReasonChange = React.useCallback(
    (key: string) => {
      const isDefaultReasonSelected = key === DEFAULT_REASON_KEY;
      const reason = !isDefaultReasonSelected
        ? reasonOptions.find((r) => r.key === key)?.label ?? ''
        : '';

      setSelectedReasonKey(key);
      reasonField.onChange(reason);
    },
    [reasonOptions, reasonField],
  );

  const handleElaborateReasonChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      const currentValue = reasonField.value || '';
      // Only call onChange if the value actually changed to prevent infinite loops
      if (value !== currentValue) {
        reasonField.onChange(value);
      }
    },
    [reasonField],
  );

  const reasonLabelText = `${t('Reason_for', 'Reason for')} ${
    actionType === 'cancel'
      ? t('cancellation', 'cancellation')
      : t('reschedule', 'reschedule')
  }`;

  return (
    <>
      {hasReasonOptions && (
        <SimpleDropdown
          locked={locked}
          required={required}
          // eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels
          label={reasonLabelText}
          placeholder={t('Select_reason', 'Select reason')}
          onChange={(option: MenuOption) => handleReasonChange(option.key)}
          // user input displayed in dropdown is not translated
          // eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels
          options={reasonOptions}
          selectedKeys={selectedReasonKey ? [selectedReasonKey] : []}
          disabled={disabled}
          // Only show error if field is touched and it's not "My reason is not listed here"
          error={Boolean(reasonFieldState.error) && reasonFieldState.isTouched}
          testId={generateTestId({base: testId, slot: 'reason-input-dropdown'})}
        />
      )}
      {(showElaborateReason || !hasReasonOptions) && (
        <Textarea
          size="medium"
          locked={locked}
          required={required}
          value={reasonField.value || ''}
          onChange={handleElaborateReasonChange}
          label={t('Elaborate_Reason', 'Elaborate Reason')}
          placeholder={t('Share_your_reason_here', 'Share your reason here')}
          disabled={disabled}
          error={Boolean(reasonFieldState.error) && reasonFieldState.isTouched}
          errorText={reasonFieldState.error?.message}
          textCountLimit={TEXT_COUNT_LIMIT}
          testId={generateTestId({base: testId, slot: 'reason-input-textarea'})}
        />
      )}
    </>
  );
};
